/**
 * Projectile Physics — Euler integration with terrain + tank collision.
 * Operates on a single projectile. The firing loop in phases.ts iterates over state.projectiles[].
 */

import { PHYSICS, WORLD, GLITCH, GLITCH_LOW_GRAVITY } from '../constants';
import type { GameState, Projectile } from '../types';
import { isPointInTerrain, getSurfaceNormal } from '../terrain/grid';
import { lookupTank } from '../tanks/registry';

export interface ProjectileResult {
  type: 'flying' | 'hit_terrain' | 'hit_tank' | 'hit_target' | 'hit_void' | 'out_of_bounds' | 'bounced';
  hitX?: number;
  hitY?: number;
  hitTankIndex?: number;
  hitTargetIndex?: number;
}

const MAX_TRAIL = 20;

export function updateSingleProjectile(proj: Projectile, state: GameState): ProjectileResult {
  // Record trail
  proj.trail.push({ x: proj.x, y: proj.y, age: 0 });
  if (proj.trail.length > MAX_TRAIL) proj.trail.shift();
  for (const p of proj.trail) p.age += 1;

  // Bounce cooldown — prevent rapid grazing bounces on rough terrain
  if (proj.behaviorState.bounceCooldown > 0) {
    proj.behaviorState.bounceCooldown--;
  }

  // Gravity (per-weapon, LOW_GRAVITY glitch reduces)
  const projGravMult = state.activeGlitch === GLITCH_LOW_GRAVITY ? GLITCH.LOW_GRAVITY_MULT : 1;
  proj.vy += proj.weapon.gravity * projGravMult;

  // Wind (horizontal force)
  if (state.wind !== 0 && !proj.ignoreWind) {
    proj.vx += state.wind;
  }

  // Move
  proj.x += proj.vx;
  proj.y += proj.vy;

  // Wall bounce
  if (proj.x < WORLD.WORLD_LEFT) {
    proj.vx *= -PHYSICS.WORLD_BOUNCE_RETENTION;
    proj.x = WORLD.WORLD_LEFT;
  } else if (proj.x > WORLD.WORLD_RIGHT) {
    proj.vx *= -PHYSICS.WORLD_BOUNCE_RETENTION;
    proj.x = WORLD.WORLD_RIGHT;
  }

  // Ceiling bounce
  if (proj.y < WORLD.WORLD_TOP) {
    proj.vy *= -PHYSICS.WORLD_BOUNCE_RETENTION;
    proj.y = WORLD.WORLD_TOP;
  }

  // Tank collision — circle vs circle (checked before terrain)
  if (!proj.ignoreTanks) {
    for (let i = 0; i < state.players.length; i++) {
      const p = state.players[i];
      if (!p.alive) continue;
      if (i === proj.owner) continue;

      const dx = proj.x - p.x;
      const dy = proj.y - p.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const ecb = lookupTank(p.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
      if (dist < proj.radius + ecb) {
        return { type: 'hit_tank', hitX: Math.round(proj.x), hitY: Math.round(proj.y), hitTankIndex: i };
      }
    }
  }

  // Target collision (Target Blast mode) — circle vs circle
  if (state.modeState.mode === 'target_blast') {
    for (let i = 0; i < state.modeState.targets.length; i++) {
      const target = state.modeState.targets[i];
      if (target.hit) continue;
      const dx = proj.x - target.x;
      const dy = proj.y - target.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < proj.radius + target.radius) {
        return { type: 'hit_target', hitX: Math.round(target.x), hitY: Math.round(target.y), hitTargetIndex: i };
      }
    }
  }

  // Terrain collision
  if (!proj.ignoreTerrain && isPointInTerrain(proj.x, proj.y, true)) {
    if (proj.bouncesRemaining > 0 && !(proj.behaviorState.bounceCooldown > 0)) {
      // Round to integers for multiplayer determinism — eliminates sub-pixel float
      // divergence in crater generation (atan2/distance at cell boundaries).
      const bounceX = Math.round(proj.x);
      const bounceY = Math.round(proj.y);
      proj.bouncesRemaining--;
      bounceOffTerrain(proj);
      proj.behaviorState.bounceCooldown = 5; // 5 frames (~83ms) between bounces
      return { type: 'bounced', hitX: bounceX, hitY: bounceY };
    }
    if (proj.bouncesRemaining <= 0) {
      return { type: 'hit_terrain', hitX: Math.round(proj.x), hitY: Math.round(proj.y) };
    }
    // During bounce cooldown, push out of terrain but don't consume a bounce
    const { nx, ny } = getSurfaceNormal(proj.x, proj.y);
    for (let i = 0; i < 20; i++) {
      proj.x += nx * 2;
      proj.y += ny * 2;
      if (!isPointInTerrain(proj.x, proj.y, true)) break;
    }
  }

  // Void collision — projectile consumed by void surface
  if (proj.y >= state.voidY) {
    return { type: 'hit_void', hitX: Math.round(proj.x), hitY: Math.round(state.voidY) };
  }

  // Out of bounds (below world)
  if (proj.y > WORLD.VIRTUAL_HEIGHT + PHYSICS.BLAST_ZONE_BUFFER) {
    return { type: 'out_of_bounds' };
  }

  return { type: 'flying' };
}

/** Reflect projectile velocity off terrain surface normal with energy retention */
function bounceOffTerrain(proj: Projectile): void {
  const { nx, ny } = getSurfaceNormal(proj.x, proj.y);

  // Reflect: v' = v - 2(v·n)n
  const dot = proj.vx * nx + proj.vy * ny;
  proj.vx = proj.vx - 2 * dot * nx;
  proj.vy = proj.vy - 2 * dot * ny;

  // Apply energy retention
  const retention = proj.weapon.bounceEnergyRetention;
  proj.vx *= retention;
  proj.vy *= retention;

  // Clamp minimum upward velocity to prevent slow dribbles
  if (proj.vy > -PHYSICS.PROJECTILE_MIN_BOUNCE_VY && proj.vy < 0) proj.vy = -PHYSICS.PROJECTILE_MIN_BOUNCE_VY;

  // Push projectile out of terrain along normal
  for (let i = 0; i < 20; i++) {
    proj.x += nx * 2;
    proj.y += ny * 2;
    if (!isPointInTerrain(proj.x, proj.y, true)) break;
  }
}
